import React, { createContext, useContext, useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient, type LoginCredentials, type RegisterData, type Token } from '../api/client';
import { type User } from '../api/types/auth';
import { usePlan } from './PlanContext';
import { useLocalStorage } from '../hooks/useLocalStorage';
import { PageLoadingSpinner } from '../components/ui/LoadingSpinner';
import { analytics } from '../services/analytics';

export interface AuthState {
  id: number;
}

export interface RegisterOptions {
  hasPendingPlan?: boolean;
}

export interface AuthContextType {
  user: User | null;
  authState: AuthState;
  isLoading: boolean;
  error: string | null;
  authFailed: boolean;
  login: (credentials: LoginCredentials) => Promise<void>;
  register: (data: RegisterData, options?: RegisterOptions) => Promise<void>;
  logout: () => void;
  clearError: () => void;
  setAuthToken: (accessToken: string, refreshToken: string) => Promise<void>;
  refreshToken: () => Promise<string | null>;
  getValidToken: () => Promise<string | null>;
  updateUser: (data: Partial<User> | User | null) => void;
}

export const AuthContext = createContext<AuthContextType | null>(null);

// Using shared LoadingSpinner component

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [isInitialized, setIsInitialized] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [authFailed, setAuthFailed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [authState, setAuthState] = useLocalStorage<AuthState>('auth_state', { id: 0 });
  const navigate = useNavigate();
  const { selectedPlan, clearPlanDetails } = usePlan();

  // Function to load user data
  const loadUserData = async () => {
    let retryCount = 0;
    const maxRetries = 3;
    let lastError: any;

    // Starting user data fetch

    while (retryCount < maxRetries) {
      try {
        // Attempting to get user data
        const response = await apiClient.getCurrentUser();
        // User data received

        // Then get settings
        // Getting user settings
        const settings = await apiClient.getUserSettings();
        // Settings retrieved successfully

        const userWithSettings: User = {
          ...response,
          theme: settings.theme as 'light' | 'dark',
          language: settings.language,
          notification_email: settings.notifications.email,
          notification_browser: settings.notifications.browser,
          login_count: response.login_count || 0,
          roles: response.roles || [],
          company: response.company
        };

        setAuthState({ id: userWithSettings.id });
        setUser(userWithSettings);
        setAuthFailed(false);
        
        // Set user ID for analytics
        analytics.setUserId(userWithSettings.id.toString());
        
        // Set user properties for analytics
        analytics.setUserProperties({
          email_verified: userWithSettings.email_verified,
          account_type: userWithSettings.account_type,
          country: userWithSettings.country,
          login_count: userWithSettings.login_count,
          roles: userWithSettings.roles.join(',')
        });
        
        return true;
      } catch (error: any) {
        lastError = error;
        
        if (error.response?.status === 401 || error.response?.status === 403) {
          // Auth error, clearing tokens
          apiClient.clearAuthToken();
          localStorage.removeItem('refresh_token');
          setAuthFailed(true);
          // Clear analytics user ID on auth failure
          analytics.setUserId('');
          return false;
        }

        await new Promise(resolve => setTimeout(resolve, 1000 * (retryCount + 1)));
        retryCount++;
      }
    }

    if (retryCount === maxRetries) {
      // Max retries reached
      apiClient.clearAuthToken();
      localStorage.removeItem('refresh_token');
      setAuthFailed(true);
      // Clear analytics user ID on max retries
      analytics.setUserId('');
      return false;
    }
  };

  // Token validation and refresh
  const getValidToken = async (): Promise<string | null> => {
    try {
      const token = apiClient.getAuthToken();
      if (!token) {
        // No token found
        return null;
      }

      const parts = token.split('.');
      if (parts.length !== 3) {
        // Invalid token format
        return null;
      }

      const tokenData = JSON.parse(atob(parts[1]));
      if (!tokenData.exp) {
        // Token missing expiration
        return null;
      }

      const expiresIn = tokenData.exp * 1000 - Date.now();
      if (expiresIn < 300000) { // 5 minutes
        // Token expiring soon, attempting refresh
        const newToken = await refreshToken();
        if (newToken) {
          return newToken;
        }
      }

      return token;
    } catch (error) {
      // Token validation error
      return null;
    }
  };

  const refreshToken = async (): Promise<string | null> => {
    const refreshToken = localStorage.getItem('refresh_token');
    if (!refreshToken) {
      // No refresh token found
      return null;
    }

    try {
      const response = await apiClient.axios.post<Token>('/api/v1/auth/refresh', {
        refresh_token: refreshToken
      });
      
      await apiClient.setAuthToken(response.data.access_token);
      if (response.data.refresh_token) {
        localStorage.setItem('refresh_token', response.data.refresh_token);
      }

      return response.data.access_token;
    } catch (error) {
      // Token refresh failed
      return null;
    }
  };

  // Check for existing session and handle token refresh
  useEffect(() => {
    const checkAuth = async () => {
      try {
        setIsLoading(true);
        const token = apiClient.getAuthToken();
        const refreshToken = localStorage.getItem('refresh_token');
        const storedAuthState = localStorage.getItem('auth_state');
        const storedId = storedAuthState ? JSON.parse(storedAuthState).id : 0;
        
        // If we have a valid auth state but no tokens, clear auth
        if (storedId > 0 && (!token || !refreshToken)) {
          // Auth state exists but no tokens, clearing auth
          localStorage.removeItem('auth_state');
          setUser(null);
          setAuthState({ id: 0 });
          setIsLoading(false);
          setIsInitialized(true);
          return;
        }
        
        // If we have no tokens and no auth state, just initialize
        if (!token || !refreshToken) {
          // No tokens found
          setIsLoading(false);
          setIsInitialized(true);
          return;
        }

        // Get valid token (will refresh if needed)
        const validToken = await getValidToken();
        if (!validToken) {
          // Could not get valid token
          setIsLoading(false);
          setIsInitialized(true);
          return;
        }

        // Load user data
        const success = await loadUserData();
        if (!success) {
          setUser(null);
          setAuthState({ id: 0 });
          navigate('/auth/login');
        }
      } catch (error: any) {
        // Error in auth check
        apiClient.clearAuthToken();
        localStorage.removeItem('refresh_token');
        // Clear analytics user ID on auth check error
        analytics.setUserId('');
        navigate('/auth/login');
      } finally {
        setIsLoading(false);
        setIsInitialized(true);
      }
    };

    checkAuth();

    // Set up token refresh interval
    const refreshInterval = setInterval(async () => {
      const token = await getValidToken();
      if (!token) {
        clearInterval(refreshInterval);
      }
    }, 240000); // 4 minutes

    return () => clearInterval(refreshInterval);
  }, [navigate]);

  const login = useCallback(async (credentials: LoginCredentials) => {
    try {
      setError(null);
      setIsLoading(true);
      const token = await apiClient.login(credentials);
      if (token.access_token && token.refresh_token) {
        await setAuthToken(token.access_token, token.refresh_token);
        
        // Track successful login
        analytics.trackLogin('email');
      } else {
        throw new Error('Invalid token response');
      }
    } catch (error: any) {
      const errorDetail = error.response?.data?.detail;
      if (typeof errorDetail === 'object') {
        switch (errorDetail.type) {
          case 'invalid_credentials':
            setError('Invalid email or password');
            break;
          case 'email_not_verified':
            setError('Please verify your email address before logging in');
            navigate('/auth/verify-email', { 
              state: { 
                message: 'Please check your email to verify your account.',
                email: credentials.username,
                hasPendingPlan: !!selectedPlan
              }
            });
            break;
          case 'account_disabled':
            setError('Your account has been disabled. Please contact support.');
            break;
          case 'server_error':
            setError('Server error. Please try again later.');
            break;
          default:
            setError(errorDetail.msg || 'Failed to login. Please try again');
        }
      } else if (error.response?.status === 500) {
        setError('Server error. Please try again later');
      } else {
        setError('Failed to login. Please try again');
      }
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [navigate, selectedPlan]);

  const register = useCallback(async (data: RegisterData, options?: RegisterOptions) => {
    try {
      setError(null);
      setIsLoading(true);
      
      const response = await apiClient.register(data);
      setUser(response.user);
      setAuthState({ id: response.user.id });
      
      // Track successful registration
      analytics.trackSignUp('email');
      
      // Set user properties for analytics
      analytics.setUserProperties({
        account_type: response.user.account_type || 'personal',
        email_verified: false,
        country: response.user.country
      });
      
      navigate('/auth/verify-email', { 
        replace: true,
        state: { 
          message: 'Please check your email to verify your account.',
          hasPendingPlan: options?.hasPendingPlan
        }
      });
    } catch (error: any) {
      const errorDetail = error.response?.data?.detail;
      if (typeof errorDetail === 'object') {
        if (errorDetail.type === 'email_registered' && errorDetail.action === 'login') {
          setError('This email is already registered and verified. Please sign in instead.');
          navigate('/auth/login', { 
            state: { 
              message: 'This email is already registered. Please sign in to continue.',
              hasPendingPlan: options?.hasPendingPlan
            }
          });
          return;
        } else if (errorDetail.type === 'email_registered') {
          try {
            const response = await apiClient.register(data);
            setUser(response.user);
            setAuthState({ id: response.user.id });
            
            // Track registration for existing unverified email
            analytics.trackSignUp('email');
            analytics.setUserProperties({
              account_type: response.user.account_type || 'personal',
              email_verified: false,
              country: response.user.country
            });
            
            navigate('/auth/verify-email', { 
              replace: true,
              state: { 
                message: 'Please check your email to verify your account.',
                hasPendingPlan: options?.hasPendingPlan
              }
            });
            return;
          } catch {
            setError('Failed to update registration. Please try again.');
            return;
          }
        } else if (errorDetail.msg && errorDetail.error) {
          if (errorDetail.error.includes('Invalid password')) {
            setError('Password must contain at least one lowercase letter, one uppercase letter, and one number');
          } else if (errorDetail.error.includes('Invalid email')) {
            setError('Please enter a valid email address');
          } else {
            setError(errorDetail.msg);
          }
        } else if (errorDetail.msg) {
          setError(errorDetail.msg);
        } else {
          setError('Registration failed. Please check your information and try again');
        }
      } else if (error.response?.status === 500) {
        setError('Server error. Please try again later');
      } else {
        setError('Registration failed. Please try again');
      }
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [navigate]);

  const logout = useCallback(() => {
    // Track logout event before clearing user data
    analytics.trackEvent('logout');
    
    // Clear user ID from analytics
    analytics.setUserId('');
    
    apiClient.clearAuthToken();
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('auth_state');
    setUser(null);
    setAuthState({ id: 0 });
    setAuthFailed(false);
    clearPlanDetails();
    navigate('/auth/login');
  }, [clearPlanDetails, navigate]);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const [isCreatingOrder, setIsCreatingOrder] = useState(false);
  const orderCreationTimeout = useRef<NodeJS.Timeout | undefined>(undefined);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (orderCreationTimeout.current) {
        clearTimeout(orderCreationTimeout.current);
      }
    };
  }, []);

  const setAuthToken = async (accessToken: string, refreshToken: string): Promise<void> => {
    // Clear any existing timeout
    if (orderCreationTimeout.current) {
      clearTimeout(orderCreationTimeout.current);
    }
    try {
      setIsLoading(true);
      // Set tokens first
      await apiClient.setAuthToken(accessToken);
      localStorage.setItem('refresh_token', refreshToken);

      // Load user data
      const success = await loadUserData();
      if (!success) {
        throw new Error('Failed to load user data');
      }

      // Check if we have a stored plan in either localStorage or context
      const storedPlan = localStorage.getItem('diskray_selected_plan');
      const plan = storedPlan ? JSON.parse(storedPlan) : selectedPlan;

      if (plan && !isCreatingOrder) {
        try {
          setIsCreatingOrder(true);
          // Set timeout to clear lock after 30 seconds
          orderCreationTimeout.current = setTimeout(() => {
            setIsCreatingOrder(false);
          }, 30000);
          
          // Checking pending plan
          const planData = {
            product_id: plan.product_id,
            period_id: plan.period_id,
            disk_space: plan.disk_space,  // Add missing disk_space field
            users_count: plan.users_count,
            selected_options: plan.selected_options,
            currency: 'EUR', // Default currency
            company_data: user?.account_type === 'business' ? {
              name: user.first_name + ' ' + user.last_name,
              code: '',
              vat_number: '',
              address: '',
              country: user.country || 'US'
            } : undefined
          };

          const result = await apiClient.checkPendingPlan(planData);
          // Pending plan result

          if (result.status === 'success' && result.redirect_url) {
            // Navigate to payment page
            navigate(result.redirect_url);
            return;
          } else {
            // Clear plan if no payment is needed
            localStorage.removeItem('diskray_selected_plan');
            clearPlanDetails();
          }
        } catch (error: any) {
          setIsCreatingOrder(false);
          if (orderCreationTimeout.current) {
            clearTimeout(orderCreationTimeout.current);
          }
          // Failed to process pending plan
          
          // If error is 404 or order already exists, go to services
          if (error.response?.status === 404 || 
              (error.response?.data?.detail && 
               typeof error.response.data.detail === 'string' &&
               (error.response.data.detail.includes('already exists') ||
                error.response.data.detail.includes('Order already exists')))) {
            // Order exists or 404, going to services
            localStorage.removeItem('diskray_selected_plan');
            clearPlanDetails();
            navigate('/dashboard/services');
            return;
          }
          
          // If we have a selected plan, go to payment page
          if (plan) {
            // Error occurred but plan exists, redirecting to payment
            navigate('/dashboard/payments/new/payment');
          } else {
            // No plan found, redirecting to services
            navigate('/dashboard/services');
          }
          return;
        }
      }

      // Only redirect to services if not on a payment completion page
      const isPaymentCompletePage = window.location.pathname.includes('/payment/complete');
      if (!isPaymentCompletePage) {
        // No plan found and not on payment complete page, redirecting to services
        navigate('/dashboard/services');
      }
    } catch (error) {
      apiClient.clearAuthToken();
      localStorage.removeItem('refresh_token');
      // Clear analytics user ID on setAuthToken error
      analytics.setUserId('');
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Enhanced user update that handles null case and ensures valid ID
  const updateUser = useCallback((data: Partial<User> | User | null): void => {
    if (!data) {
      logout();
      return;
    }
    
    // Track email verification if it changes
    if ('email_verified' in data && data.email_verified === true && user && !user.email_verified) {
      analytics.trackEvent('email_verified');
      analytics.setUserProperties({
        email_verified: true
      });
    }
    
    // If we have an ID, update auth state
    if ('id' in data && typeof data.id === 'number') {
      // Update auth state in localStorage
      localStorage.setItem('auth_state', JSON.stringify({ id: data.id }));
      setAuthState({ id: data.id });
    }
    
    // Update user data
    if (!user) {
      // Create new user with required fields
      const newUser: User = {
        id: ('id' in data && typeof data.id === 'number') ? data.id : 0,
        email: data.email || '',
        first_name: data.first_name || '',
        last_name: data.last_name || '',
        active: data.active || false,
        account_type: data.account_type || 'personal',
        email_verified: data.email_verified || false,
        login_count: data.login_count || 0,
        roles: data.roles || [],
        ...data
      };
      setUser(newUser);
      // Update auth state
      localStorage.setItem('auth_state', JSON.stringify({ id: newUser.id }));
      setAuthState({ id: newUser.id });
      
      // Set analytics user ID and properties for new user
      if (newUser.id > 0) {
        analytics.setUserId(newUser.id.toString());
        analytics.setUserProperties({
          email_verified: newUser.email_verified,
          account_type: newUser.account_type,
          country: newUser.country,
          login_count: newUser.login_count,
          roles: newUser.roles.join(',')
        });
      }
    } else {
      // Update existing user
      const updatedUser = { ...user, ...data };
      setUser(updatedUser);
      // Update auth state
      localStorage.setItem('auth_state', JSON.stringify({ id: updatedUser.id }));
      setAuthState({ id: updatedUser.id });
      
      // Update analytics user properties if relevant fields changed
      const propsToUpdate: any = {};
      if ('account_type' in data) propsToUpdate.account_type = data.account_type;
      if ('country' in data) propsToUpdate.country = data.country;
      if ('login_count' in data) propsToUpdate.login_count = data.login_count;
      if ('roles' in data) propsToUpdate.roles = data.roles?.join(',');
      
      if (Object.keys(propsToUpdate).length > 0) {
        analytics.setUserProperties(propsToUpdate);
      }
    }
  }, [user, logout]);

  const value: AuthContextType = useMemo(() => ({
    user,
    authState,
    isLoading,
    error,
    authFailed,
    login,
    register,
    logout,
    clearError,
    setAuthToken,
    refreshToken,
    getValidToken,
    updateUser
  }), [user, authState, isLoading, error, authFailed, login, register, logout, clearError, setAuthToken, refreshToken, getValidToken, updateUser]);

  // Only render children after auth is initialized
  if (!isInitialized) {
    return <PageLoadingSpinner />;
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuthContext() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  return context;
}

export function useAuth() {
  return useAuthContext();
}
